import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { signInWithEmailAndPassword, onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase';
import { useForm } from 'react-hook-form';
import { AuthLayout } from '../components/auth';
import { FormField, Button } from '../components/forms';
import { checkUserFranchiseeStatus } from '../services/signupService';
import { getUserInfo } from '../services/userService';

const LoginPage = () => {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoginInProgress, setIsLoginInProgress] = useState(false); // Track if login is in progress
  const navigate = useNavigate();

  const { register, handleSubmit, formState: { errors } } = useForm();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      // Only redirect if user is authenticated AND login is not in progress
      // This prevents redirect during the login process where we need to check user status
      if (user && !isLoginInProgress) {
        try {
          // Check if user needs to reset password
          const idToken = await user.getIdToken();
          const userInfo = await getUserInfo(idToken);
          
          if (userInfo.passwordResetRequired) {
            navigate('/change-password'); // Redirect to change password if required
          } else {
            navigate('/'); // If user is logged in and no password reset required, redirect to home
          }
        } catch (error) {
          console.error('Error checking user info:', error);
          navigate('/'); // Default to home on error
        }
      }
      setLoading(false); // Finish loading check
    });
    return () => unsubscribe(); // Cleanup subscription
  }, [navigate, isLoginInProgress]);

  const handleLogin = async (data) => {
    setError('');
    setIsSubmitting(true);
    setIsLoginInProgress(true); // Mark login as in progress

    try {
      // First authenticate with Firebase
      const userCredential = await signInWithEmailAndPassword(auth, data.email, data.password);
      const user = userCredential.user;
      const userInfo = await getUserInfo(await user.getIdToken());
      
      if (userInfo.passwordResetRequired) {
        navigate('/change-password'); // Redirect to change password if required
        return;
      } 
      // Check if user has complete franchisee account
      const statusResult = await checkUserFranchiseeStatus(user.uid);

      if (!statusResult.hasCompleteAccount) {
        // User exists in Firebase but doesn't have complete franchisee setup
        let errorMessage = 'Your account setup is incomplete. ';

        if (statusResult.message.includes('complete signup process') || statusResult.message.includes('franchisee setup')) {
          errorMessage += 'Please go to the signup page to complete your registration.';
        } else {
          errorMessage += 'Please contact support for assistance.';
        }

        setError(errorMessage);

        // Sign out the user since they can't access the app
        await auth.signOut();
        setIsLoginInProgress(false); // Reset login progress
        return;
      }

      // User has complete account, proceed to app
      setIsLoginInProgress(false); // Mark login as complete
      navigate('/');
    } catch (err) {
      if (err.message && err.message.includes('account setup is incomplete')) {
        // This is our custom error, don't override it
        setError(err.message);
      } else if (err.message && err.message.includes('suspended')) {
        // Handle suspended account error
        setError(err.message);
        // Sign out the user since they can't access the app
        await auth.signOut();
      } else {
        setError('Failed to log in. Please check your email and password.');
      }
      console.error("Login error:", err);
      setIsLoginInProgress(false); // Reset on error
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <AuthLayout title="Sign in to Loope" subtitle="Please wait while we check your authentication status">
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Sign in to Loope"
      platform="Franchisee Portal"
      subtitle={
        <>
          Don't have an account?{' '}
          <Link to="/signup" className="font-medium text-green-600 hover:text-green-500 underline">
            Sign up as a franchisee
          </Link>
        </>
      }
    >
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-start">
          <svg className="h-5 w-5 text-red-400 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
          <span>{error}</span>
        </div>
      )}

      <form onSubmit={handleSubmit(handleLogin)} className="space-y-6">
        <FormField
          label="Email Address"
          name="email"
          type="email"
          register={register}
          validation={{
            required: "Email is required",
            pattern: {
              value: /\S+@\S+\.\S+/,
              message: "Please enter a valid email address"
            }
          }}
          error={errors.email}
          placeholder="your.email@example.com"
          required
        />

        <FormField
          label="Password"
          name="password"
          type="password"
          register={register}
          validation={{ required: "Password is required" }}
          error={errors.password}
          placeholder="Enter your password"
          required
        />

        <div className="flex justify-end">
          <Link
            to="/forgot-password"
            className="text-sm font-medium text-green-600 hover:text-green-500"
          >
            Forgot your password?
          </Link>
        </div>

        <Button
          type="submit"
          loading={isSubmitting}
          className="w-full"
        >
          {isSubmitting ? 'Signing In...' : 'Sign In'}
        </Button>
      </form>
    </AuthLayout>
  );
};

export default LoginPage;
